import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSession } from '@/domains/auth/services/auth.service';
import {
  fetchDriverProfile,
  fetchDriverDeliveries,
  setAvailability,
  fetchDriverStats,
  fetchDriverDocuments,
  acceptDelivery,
  completeDelivery,
} from '@/domains/driver/services/driver.service';
import { getErrorMessage } from '@/utils/apiErrorHandler';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import {
  PremiumDashboardLayout,
  StatCard,
  EmptyState,
  LoadingSpinner,
} from '@/components/dashboard/PremiumDashboardLayout';
import type { Delivery, DriverDocument, DriverProfile, DriverStats } from '@/domains/driver/services/driver.service';

const TABS = [
  { id: 'accueil', label: 'Accueil', icon: 'ri-home-line' },
  { id: 'missions', label: 'Missions', icon: 'ri-truck-line' },
  { id: 'revenus', label: 'Revenus', icon: 'ri-wallet-3-line' },
  { id: 'documents', label: 'Documents', icon: 'ri-file-shield-line' },
  { id: 'profil', label: 'Profil', icon: 'ri-user-line' },
];

const PAGE_TITLES: Record<string, string> = {
  accueil: 'Tableau de bord pro',
  missions: 'Missions professionnelles',
  revenus: 'Revenus & assurances',
  documents: 'Dossier administratif',
  profil: 'Mon profil professionnel',
};

const DOC_STATUS: Record<string, { label: string; className: string }> = {
  pending: { label: 'En attente', className: 'bg-amber-50 text-amber-700 border-amber-100' },
  valid: { label: 'Validé', className: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  dismiss: { label: 'Rejeté', className: 'bg-rose-50 text-rose-700 border-rose-100' },
  suspended: { label: 'Suspendu', className: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export default function LivreurProDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('accueil');
  const [error, setError] = useState('');
  const [isAvailable, setIsAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [driver, setDriver] = useState<DriverProfile | null>(null);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [documents, setDocuments] = useState<DriverDocument[]>([]);
  const [stats, setStats] = useState<DriverStats | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const isPending = driver?.status === 'pending';
  const isActive = driver?.status === 'active';

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [profile, deliveryList, statsData, docs] = await Promise.all([
        fetchDriverProfile(),
        fetchDriverDeliveries(),
        fetchDriverStats(),
        fetchDriverDocuments(),
      ]);
      setDriver(profile);
      setDeliveries(deliveryList);
      setStats(statsData);
      setDocuments(docs);
      setIsAvailable(profile.status === 'active');
      setError('');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!getSession()) {
      navigate('/login');
      return;
    }
    loadData();
  }, [navigate, loadData]);

  const handleAvailabilityToggle = async () => {
    if (!isActive) return;
    try {
      await setAvailability(!isAvailable);
      setIsAvailable(!isAvailable);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleDeliveryAction = async (deliveryId: number, action: 'accept' | 'complete') => {
    setActionLoading(deliveryId);
    try {
      if (action === 'accept') await acceptDelivery(deliveryId);
      else await completeDelivery(deliveryId);
      await loadData();
    } catch {
      setDeliveries((prev) =>
        prev.map((d) => (d.id === deliveryId ? { ...d, statut: action === 'accept' ? 'en_transit' : 'livré' } : d)),
      );
    } finally {
      setActionLoading(null);
    }
  };

  const pendingMissions = deliveries.filter((d) => d.statut === 'en_attente');
  const activeMissions = deliveries.filter((d) => d.statut === 'en_transit');
  const pendingDocs = documents.filter((d) => d.status === 'pending').length;

  return (
    <PremiumDashboardLayout
      tabs={TABS.map((t) => {
        if (t.id === 'missions') return { ...t, badge: pendingMissions.length };
        if (t.id === 'documents') return { ...t, badge: pendingDocs };
        return t;
      })}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      pageTitle={PAGE_TITLES[activeTab] ?? 'Dashboard Pro'}
      roleLabel="Livreur Professionnel"
      accent="blue"
      userName={driver ? `${driver.firstnames} ${driver.lastname}` : undefined}
      userZone={driver?.zone?.name}
      userInitial={driver?.firstnames?.[0]}
      mobileNavTabIds={['accueil', 'missions', 'documents', 'profil']}
    >
      {error && <ErrorAlert error={error} onClose={() => setError('')} />}

      {isPending && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start space-x-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600 shrink-0">
            <i className="ri-time-line text-lg" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-amber-900">Compte en attente de validation</h3>
            <p className="text-xs text-amber-700 mt-1">
              Votre dossier est en cours d'examen par l'équipe ONAPILA. Vous pourrez accepter des missions une fois votre compte validé.
            </p>
          </div>
        </div>
      )}

      {activeTab === 'accueil' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 rounded-2xl p-6 text-white border border-slate-800">
            <span className="bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
              Livreur Professionnel
            </span>
            <h2 className="text-base font-bold tracking-tight mt-2">Espace professionnel certifié</h2>
            <p className="text-xs text-slate-400 mt-1">
              Livraisons express, spécialisées et interurbaines avec assurance intégrée.
            </p>
          </div>

          <div className={`bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex items-center justify-between ${!isActive ? 'opacity-60' : ''}`}>
            <div>
              <h3 className="text-sm font-bold text-slate-800">Disponibilité</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {!isActive ? 'Activez votre compte pour recevoir des missions' : isAvailable ? 'En ligne — missions actives' : 'Hors ligne'}
              </p>
            </div>
            <button
              disabled={!isActive}
              onClick={handleAvailabilityToggle}
              className={`px-5 py-2.5 rounded-xl text-xs font-bold ${
                isAvailable && isActive ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'
              } disabled:cursor-not-allowed`}
            >
              {isAvailable ? 'En ligne' : 'Hors ligne'}
            </button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard icon="ri-truck-line" label="En cours" value={activeMissions.length} color="blue" />
              <StatCard icon="ri-time-line" label="En attente" value={pendingMissions.length} color="amber" />
              <StatCard icon="ri-money-dollar-circle-line" label="Ce mois" value={`${((stats?.month_earnings ?? 0) / 1000).toFixed(0)}k`} color="emerald" />
              <StatCard icon="ri-star-line" label="Note" value={stats?.average_rating?.toFixed(1) ?? '—'} color="indigo" />
            </div>
          )}
        </div>
      )}

      {activeTab === 'missions' && (
        <div className="space-y-6">
          {!isActive ? (
            <EmptyState icon="ri-lock-line" message="Validez votre compte pour accéder aux missions" />
          ) : loading ? (
            <LoadingSpinner />
          ) : deliveries.length === 0 ? (
            <EmptyState icon="ri-truck-line" message="Aucune mission disponible" />
          ) : (
            <div className="space-y-3">
              {deliveries.map((d) => (
                <ProMissionCard
                  key={d.id}
                  delivery={d}
                  actionLoading={actionLoading}
                  onAction={handleDeliveryAction}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'revenus' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <StatCard icon="ri-sun-line" label="Aujourd'hui" value={`${(stats?.today_earnings ?? 0).toLocaleString()} GNF`} color="blue" />
            <StatCard icon="ri-calendar-line" label="Semaine" value={`${(stats?.week_earnings ?? 0).toLocaleString()} GNF`} color="indigo" />
            <StatCard icon="ri-calendar-check-line" label="Mois" value={`${(stats?.month_earnings ?? 0).toLocaleString()} GNF`} color="emerald" />
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-2">Assurance intégrée</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              Couverture automatique des colis transportés et protection en cas d'accident pendant les missions actives.
            </p>
            <div className="mt-4 flex items-center space-x-2 text-xs text-emerald-700 bg-emerald-50 px-3 py-2 rounded-xl w-fit">
              <i className="ri-shield-check-line" />
              <span>Statut assurance : {isActive ? 'Active' : 'En attente de validation'}</span>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="space-y-4">
          {loading ? (
            <LoadingSpinner />
          ) : documents.length === 0 ? (
            <EmptyState icon="ri-file-shield-line" message="Aucun document enregistré" />
          ) : (
            documents.map((doc) => {
              const statusInfo = DOC_STATUS[doc.status] ?? DOC_STATUS.pending;
              return (
                <div key={doc.id} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-800">{doc.category?.name ?? 'Document'}</h3>
                      <p className="text-xs text-slate-500 mt-1">
                        Valide du {new Date(doc.started_at).toLocaleDateString('fr-FR')} au{' '}
                        {new Date(doc.ended_at).toLocaleDateString('fr-FR')}
                      </p>
                      <p className="text-xs text-slate-400 mt-1 truncate max-w-xs">{doc.file_url}</p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${statusInfo.className}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {activeTab === 'profil' && driver && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Informations</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-xs text-slate-400">Nom</p><p className="font-medium">{driver.firstnames} {driver.lastname}</p></div>
              <div><p className="text-xs text-slate-400">Téléphone</p><p className="font-medium">{driver.phone}</p></div>
              <div><p className="text-xs text-slate-400">Email</p><p className="font-medium">{driver.email ?? '—'}</p></div>
              <div>
                <p className="text-xs text-slate-400">Statut compte</p>
                <p className={`font-medium capitalize ${isPending ? 'text-amber-600' : isActive ? 'text-emerald-600' : 'text-slate-600'}`}>
                  {driver.status}
                </p>
              </div>
            </div>
          </div>

          {driver.vehicleCapacity && (
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <h3 className="text-sm font-bold text-slate-800 mb-4">Véhicule professionnel</h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div><p className="text-xs text-slate-400">Type</p><p className="font-medium">{driver.vehicleCapacity.vehicleType.name}</p></div>
                <div><p className="text-xs text-slate-400">Poids</p><p className="font-medium">{driver.vehicleCapacity.max_weight} kg</p></div>
                <div><p className="text-xs text-slate-400">Volume</p><p className="font-medium">{driver.vehicleCapacity.max_volume} m³</p></div>
              </div>
            </div>
          )}

          {driver.coverageZones && driver.coverageZones.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <h3 className="text-sm font-bold text-slate-800 mb-3">Zones couvertes</h3>
              <div className="flex flex-wrap gap-2">
                {driver.coverageZones.map((z) => (
                  <span key={z.id} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-xl text-xs font-semibold">{z.name}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </PremiumDashboardLayout>
  );
}

function ProMissionCard({
  delivery,
  actionLoading,
  onAction,
}: {
  delivery: Delivery;
  actionLoading: number | null;
  onAction: (id: number, action: 'accept' | 'complete') => void;
}) {
  const cmd = delivery.commande;
  const isLoading = actionLoading === delivery.id;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
      <div className="flex justify-between mb-3">
        <div>
          <p className="text-xs font-bold text-blue-600">{cmd?.code_suivi}</p>
          <p className="text-sm font-semibold text-slate-800 mt-1">{cmd?.adresse_livraison}</p>
        </div>
        <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-blue-50 text-blue-700">
          {delivery.statut.replace('_', ' ')}
        </span>
      </div>
      <div className="text-xs text-slate-500 mb-3">
        {cmd?.prix_estime && <span className="mr-4"><i className="ri-money-dollar-circle-line mr-1" />{cmd.prix_estime.toLocaleString()} GNF</span>}
        {cmd?.distance_km && <span><i className="ri-route-line mr-1" />{cmd.distance_km} km</span>}
      </div>
      <div className="flex gap-2">
        {delivery.statut === 'en_attente' && (
          <button disabled={isLoading} onClick={() => onAction(delivery.id, 'accept')} className="flex-1 py-2 bg-blue-600 text-white text-xs font-bold rounded-xl disabled:opacity-50">
            Accepter
          </button>
        )}
        {delivery.statut === 'en_transit' && (
          <button disabled={isLoading} onClick={() => onAction(delivery.id, 'complete')} className="flex-1 py-2 bg-emerald-600 text-white text-xs font-bold rounded-xl disabled:opacity-50">
            Confirmer livraison
          </button>
        )}
      </div>
    </div>
  );
}
